import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LoaderCircle } from 'lucide-preact';
import { useMemo, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { setQueryParams } from '@/hooks/route';
import { getProductsQueryOptions, patchUserProductMutationFn, postUserProductMutationFn } from '@/queries/products';
import { ProductInterval } from '@/queries/products/types';
import { trackMP } from '@/shared/mp';

import { Button } from '../Buttons';
import ProductEligibilityResult from './ProductEligibilityResult';
import ProductFAQButton from './ProductFAQButton';
import { ProductFeaturesList, ProductFeaturesListItem } from './ProductFeatureList';
import ProductIntervalSelector from './ProductIntervalSelector';
import ProductPrice from './ProductPrice';
import { setProductsPromptModalState } from './ProductsPromptModal/redux/actions';
import { handlePatchUserProductSuccess, parseBenefits } from './utils';

interface EducatorSubscriptionProps {
  entryPoint?: string; // for analytics only
}

const EducatorSubscription = ({ entryPoint }: EducatorSubscriptionProps) => {
  const [productInterval, setProductInterval] = useState<ProductInterval>('month');
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { data: products } = useQuery({
    ...getProductsQueryOptions({
      lookupKey: 'monthly-educator,annual-educator',
    }),
    select: (data) => data.list,
  });

  const monthlyProduct = products?.find((product) => product.productLookupKey === 'monthly-educator');
  const yearlyProduct = products?.find((product) => product.productLookupKey === 'annual-educator');

  const product = productInterval === 'year' ? yearlyProduct : monthlyProduct;

  const benefits = useMemo(() => parseBenefits(product), [product]);

  const { mutate: checkout, isPending } = useMutation({
    mutationFn: postUserProductMutationFn,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  const { mutate: patchUserProduct, isPending: isChangingTier } = useMutation({
    mutationFn: patchUserProductMutationFn,
    onSuccess: (data, variables) => {
      handlePatchUserProductSuccess(queryClient)(data, variables);
      setQueryParams({ ppt: undefined });
      dispatch(setProductsPromptModalState(null));
    },
  });

  return (
    <div className="flex w-full max-w-[336px] flex-col items-center justify-center rounded-2xl bg-beige-light shadow-[0px_4px_12px_0px_rgba(0,0,0,0.20)]">
      <div className="mt-3 flex w-full flex-col items-center gap-4">
        <div className="w-full px-4">
          <ProductFAQButton className="mb-2.5 text-black" />
          <h4 className="preflight preflight-h4 text-2xl/7 font-bold text-green-900">Educator Membership</h4>
          <p className="preflight preflight-p text-sm text-stone-500">Unlimited learning on top of existing access.</p>
        </div>
        <div className="flex w-full items-center justify-between gap-2 px-4">
          <ProductPrice currency={product?.price.currency} price={product?.price.price} />
          <ProductIntervalSelector value={productInterval} onChange={setProductInterval} className="bg-white" />
        </div>
        <ProductFeaturesList className="w-full px-4">
          {benefits.map((benefit, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <ProductFeaturesListItem key={index.toString()}>{benefit}</ProductFeaturesListItem>
          ))}
        </ProductFeaturesList>
      </div>
      <div className="w-full p-4">
        <div className="px-4">
          <Button
            disabled={
              isPending ||
              isChangingTier ||
              (!product?.eligibility?.allowed && !product?.eligibility?.tierChangeableUserProductId)
            }
            size="manual"
            className="w-full px-4 py-3 text-base/none font-bold"
            onClick={() => {
              if (!product) return;
              if (product.eligibility?.tierChangeableUserProductId) {
                patchUserProduct({
                  userProductId: product.eligibility.tierChangeableUserProductId,
                  action: 'tierChange',
                  targetProductId: product._id,
                  targetPriceId: product.price.priceId,
                });
              } else {
                checkout({
                  productId: product._id,
                  priceId: product.price.priceId,
                  successPath: window.location.pathname,
                  cancelPath: window.location.pathname,
                });
                trackMP('request-checkout', {
                  productId: product._id,
                  priceId: product.price.priceId,
                  productLookupKey: product.productLookupKey,
                  entryPoint,
                });
              }
            }}
          >
            {isChangingTier ? (
              <LoaderCircle className="size-4 animate-spin" />
            ) : product?.eligibility?.tierChangeableUserProductId ? (
              'Upgrade Plan'
            ) : (
              'Select Plan'
            )}
          </Button>
          {product && !product.eligibility?.tierChangeableUserProductId && (
            <ProductEligibilityResult result={product.eligibility} className="mt-3" />
          )}
        </div>

        <p className="preflight preflight-p mt-2 text-center text-sm text-stone-500">Cancel Anytime</p>
      </div>
    </div>
  );
};

export default EducatorSubscription;
